package dev.terrakok.mapitan

import androidx.compose.ui.graphics.Color
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.unit.DpRect
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import dev.terrakok.mapitan.domain.Document
import dev.terrakok.mapitan.domain.DocumentSerialization
import dev.terrakok.mapitan.domain.LineStyle
import dev.terrakok.mapitan.domain.Project
import dev.terrakok.mapitan.domain.extractEncodedProjectFromDeeplink
import dev.terrakok.mapitan.domain.geojson.MapKeys
import dev.terrakok.mapitan.domain.geojson.areaLinesGeoJson
import dev.terrakok.mapitan.domain.geojson.areaPointsGeoJson
import dev.terrakok.mapitan.domain.geojson.areaPolygonsGeoJson
import dev.terrakok.mapitan.domain.geojson.areaSelectionGeoJson
import dev.terrakok.mapitan.domain.geojson.draftCursorLineGeoJson
import dev.terrakok.mapitan.domain.geojson.findDocumentObjectIds
import dev.terrakok.mapitan.domain.geojson.labelSelectionGeoJson
import dev.terrakok.mapitan.domain.geojson.labelsGeoJson
import dev.terrakok.mapitan.domain.geojson.pinSelectionGeoJson
import dev.terrakok.mapitan.domain.geojson.pinsGeoJson
import dev.terrakok.mapitan.domain.geojson.trackLinesGeoJson
import dev.terrakok.mapitan.domain.geojson.trackPointsGeoJson
import dev.terrakok.mapitan.domain.geojson.trackSelectionGeoJson
import dev.terrakok.mapitan.domain.utils.toHexRgb
import dev.terrakok.mapitan.theme.AppTheme
import dev.terrakok.mapitan.theme.LocalThemeIsDark
import dev.terrakok.mapitan.ui.Icons
import dev.terrakok.mapitan.ui.components.AREA_BORDER_HIT_WIDTH
import dev.terrakok.mapitan.ui.components.LABEL_BUBBLE_SIZE
import dev.terrakok.mapitan.ui.components.LABEL_BUBBLE_STRETCH
import dev.terrakok.mapitan.ui.components.LABEL_SELECTION_STRETCH
import dev.terrakok.mapitan.ui.components.LabelBubblePainter
import dev.terrakok.mapitan.ui.components.MarkerPainter
import dev.terrakok.mapitan.ui.components.SELECTION_FRAME_OFFSET
import dev.terrakok.mapitan.ui.components.SELECTION_FRAME_SIZE
import dev.terrakok.mapitan.ui.components.StrokeFramePainter
import dev.terrakok.mapitan.ui.components.TRACK_HIT_AREA_WIDTH
import dev.terrakok.mapitan.ui.components.TRACK_POINT_SIZE
import dev.terrakok.mapitan.ui.components.TRACK_SELECTION_WIDTH
import kotlinx.coroutines.launch
import mapitan.sharedui.generated.resources.Res
import org.maplibre.compose.expressions.dsl.Feature
import org.maplibre.compose.expressions.dsl.case
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.convertToColor
import org.maplibre.compose.expressions.dsl.eq
import org.maplibre.compose.expressions.dsl.format
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.expressions.dsl.offset
import org.maplibre.compose.expressions.dsl.span
import org.maplibre.compose.expressions.dsl.switch
import org.maplibre.compose.expressions.value.IconPitchAlignment
import org.maplibre.compose.expressions.value.IconRotationAlignment
import org.maplibre.compose.expressions.value.IconTextFit
import org.maplibre.compose.expressions.value.LineCap
import org.maplibre.compose.expressions.value.LineJoin
import org.maplibre.compose.expressions.value.StringValue
import org.maplibre.compose.expressions.value.SymbolAnchor
import org.maplibre.compose.expressions.value.TextPitchAlignment
import org.maplibre.compose.expressions.value.TextRotationAlignment
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.interaction.KeyModifier
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.interaction.ModifierMatch.Containing
import org.maplibre.compose.interaction.ScrollResponse
import org.maplibre.compose.layers.FillLayer
import org.maplibre.compose.layers.LineLayer
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.map.MapUiOptions
import org.maplibre.compose.map.rememberMapState
import org.maplibre.compose.sources.GeoJsonOptions
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.style.BaseStyle
import org.maplibre.compose.util.DpPadding
import org.maplibre.spatialk.geojson.Position

@Composable
fun App(
    deeplink: String? = null,
    onThemeChanged: @Composable (isDark: Boolean) -> Unit = {}
) = AppTheme(onThemeChanged) {
    val isDark = LocalThemeIsDark.current.value
    val mapStyleFile = remember(isDark) {
        if (isDark) "files/dark-matter.json" else "files/bright.json"
    }
    val initialDocument = extractEncodedProjectFromDeeplink(deeplink)
        ?.let { DocumentSerialization.decode(it) } ?: Document()

    val viewModel = viewModel {
        ProjectViewModel(Project(initialDocument))
    }
    val doc by viewModel.document.collectAsStateWithLifecycle()
    val selectedColor by viewModel.selectedColor.collectAsStateWithLifecycle(ProjectViewModel.DefaultColor)
    val selectedLineThickness by viewModel.selectedLineThickness.collectAsStateWithLifecycle(ProjectViewModel.DefaultLineThickness)
    val selectedLineStyle by viewModel.selectedLineStyle.collectAsStateWithLifecycle(ProjectViewModel.DefaultLineStyle)
    val labelText by viewModel.labelText.collectAsStateWithLifecycle("")
    val selectedObject by viewModel.selectedObject.collectAsStateWithLifecycle()
    val draggingPin by viewModel.draggingPin.collectAsStateWithLifecycle()
    val draggingLabel by viewModel.draggingLabel.collectAsStateWithLifecycle()
    val trackDraft by viewModel.trackDraft.collectAsStateWithLifecycle()
    val draggingTrackPoint by viewModel.draggingTrackPoint.collectAsStateWithLifecycle()
    val areaDraft by viewModel.areaDraft.collectAsStateWithLifecycle()
    val draggingAreaPoint by viewModel.draggingAreaPoint.collectAsStateWithLifecycle()
    val canUndo by viewModel.canUndo.collectAsState()
    val canRedo by viewModel.canRedo.collectAsState()
    val lock by viewModel.mapToolIsLocked.collectAsStateWithLifecycle()
    val mode by viewModel.mode.collectAsState()
    val selectionFrameColor = MaterialTheme.colorScheme.primary
    val coroutineScope = rememberCoroutineScope()

    val allDocLabelColors = doc.labels.map { it.color }.distinct().sortedBy { it.value }

    // cursorPosition is shared between map layers (rendering) and AppContent (LaunchedEffect updates)
    var cursorPosition by remember { mutableStateOf<Position?>(null) }

    val mapState = rememberMapState(
        baseStyle = BaseStyle.Uri(Res.getUri(mapStyleFile)),
    ) {
        // Area layers
        val areaSelectionSource = rememberGeoJsonSource(
            selectedObject.areaSelectionGeoJson(areaDraft, draggingAreaPoint)
        )
        LineLayer(
            id = MapKeys.AREA_SELECTION_LAYER_ID,
            source = areaSelectionSource,
            color = const(selectionFrameColor),
            width = const(1.5.dp),
            cap = const(LineCap.Round),
            join = const(LineJoin.Round),
        )

        val areaPolygonSource = rememberGeoJsonSource(
            data = doc.areaPolygonsGeoJson(areaDraft, draggingAreaPoint),
            options = InteractiveGeoJsonOptions,
        )
        FillLayer(
            id = MapKeys.AREA_FILL_LAYER_ID,
            source = areaPolygonSource,
            color = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
            opacity = const(0.3f),
        )

        val areaLineSource = rememberGeoJsonSource(
            data = doc.areaLinesGeoJson(areaDraft, draggingAreaPoint),
            options = InteractiveGeoJsonOptions,
        )
        LineStyle.entries.forEach { style ->
            LineLayer(
                id = MapKeys.areaLineLayerId(style),
                source = areaLineSource,
                filter = Feature[MapKeys.LINE_STYLE_PROPERTY].cast<StringValue>() eq const(style.name),
                color = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
                width = Feature[MapKeys.THICKNESS_PROPERTY].cast(),
                dasharray = style.dashArray?.let { const(it) },
                cap = const(if (style == LineStyle.Dotted) LineCap.Round else LineCap.Butt),
                join = const(LineJoin.Round),
            )
        }

        val areaDraftCursorSource = rememberGeoJsonSource(areaDraft.draftCursorLineGeoJson(cursorPosition))
        LineLayer(
            id = "${MapKeys.AREA_LINE_LAYER_ID}-draft-cursor",
            source = areaDraftCursorSource,
            color = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
            width = Feature[MapKeys.THICKNESS_PROPERTY].cast(),
            dasharray = const(listOf(2, 2)),
            cap = const(LineCap.Butt),
            join = const(LineJoin.Round),
        )

        val areaPointSource = rememberGeoJsonSource(
            data = doc.areaPointsGeoJson(selectedObject?.id, areaDraft, draggingAreaPoint),
            options = InteractiveGeoJsonOptions,
        )
        val areaPointPainter = remember { MarkerPainter() }
        SymbolLayer(
            id = MapKeys.AREA_POINT_LAYER_ID,
            source = areaPointSource,
            iconImage = image(
                value = areaPointPainter,
                size = TRACK_POINT_SIZE,
                drawAsSdf = true,
            ),
            iconColor = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
            iconAnchor = const(SymbolAnchor.Center),
            iconAllowOverlap = const(true),
            iconIgnorePlacement = const(true),
        )

        // Track layers
        val trackSelectionSource = rememberGeoJsonSource(
            selectedObject.trackSelectionGeoJson(trackDraft, draggingTrackPoint)
        )
        LineLayer(
            id = MapKeys.TRACK_SELECTION_LAYER_ID,
            source = trackSelectionSource,
            color = const(selectionFrameColor.copy(alpha = 0.4f)),
            width = const(TRACK_SELECTION_WIDTH),
            cap = const(LineCap.Round),
            join = const(LineJoin.Round),
        )

        val trackLineSource = rememberGeoJsonSource(
            data = doc.trackLinesGeoJson(trackDraft, draggingTrackPoint),
            options = InteractiveGeoJsonOptions,
        )
        LineStyle.entries.forEach { style ->
            LineLayer(
                id = MapKeys.trackLineLayerId(style),
                source = trackLineSource,
                filter = Feature[MapKeys.LINE_STYLE_PROPERTY].cast<StringValue>() eq const(style.name),
                color = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
                width = Feature[MapKeys.THICKNESS_PROPERTY].cast(),
                dasharray = style.dashArray?.let { const(it) },
                cap = const(if (style == LineStyle.Dotted) LineCap.Round else LineCap.Butt),
                join = const(LineJoin.Round),
            )
        }

        val draftCursorSource = rememberGeoJsonSource(trackDraft.draftCursorLineGeoJson(cursorPosition))
        LineLayer(
            id = MapKeys.DRAFT_CURSOR_LAYER_ID,
            source = draftCursorSource,
            color = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
            width = Feature[MapKeys.THICKNESS_PROPERTY].cast(),
            dasharray = const(listOf(2, 2)),
            cap = const(LineCap.Butt),
            join = const(LineJoin.Round),
        )

        val trackPointSource = rememberGeoJsonSource(
            data = doc.trackPointsGeoJson(selectedObject?.id, trackDraft, draggingTrackPoint),
            options = InteractiveGeoJsonOptions,
        )
        val trackPointPainter = remember { MarkerPainter() }
        SymbolLayer(
            id = MapKeys.TRACK_POINT_LAYER_ID,
            source = trackPointSource,
            iconImage = image(
                value = trackPointPainter,
                size = TRACK_POINT_SIZE,
                drawAsSdf = true,
            ),
            iconColor = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
            iconAnchor = const(SymbolAnchor.Center),
            iconAllowOverlap = const(true),
            iconIgnorePlacement = const(true),
        )

        // Pin layers
        val pointSource = rememberGeoJsonSource(
            data = doc.pinsGeoJson(draggingPin),
            options = InteractiveGeoJsonOptions,
        )
        val pinPainter = rememberVectorPainter(Icons.MapPin)

        SymbolLayer(
            id = MapKeys.PIN_LAYER_ID,
            source = pointSource,
            iconImage = image(
                value = pinPainter,
                size = DpSize(60.dp, 60.dp),
                drawAsSdf = true,
            ),
            iconColor = Feature[MapKeys.COLOR_PROPERTY].convertToColor(),
            iconSize = const(0.5f),
            iconAnchor = const(SymbolAnchor.Bottom),
            iconAllowOverlap = const(true),
        )

        val selectedPinSource = rememberGeoJsonSource(selectedObject.pinSelectionGeoJson(draggingPin))
        val selectionPainter = remember(selectionFrameColor) { StrokeFramePainter(selectionFrameColor) }

        SymbolLayer(
            id = MapKeys.SELECTION_LAYER_ID,
            source = selectedPinSource,
            iconImage = image(
                value = selectionPainter,
                size = SELECTION_FRAME_SIZE,
            ),
            iconAnchor = const(SymbolAnchor.Bottom),
            iconOffset = const(SELECTION_FRAME_OFFSET),
            iconAllowOverlap = const(true),
            iconIgnorePlacement = const(true),
        )

        // Label layers
        val labelSource = rememberGeoJsonSource(
            data = doc.labelsGeoJson(draggingLabel),
            options = InteractiveGeoJsonOptions,
        )
        val labelBubbleImages = allDocLabelColors.associateWith { color ->
            image(
                value = remember(color) { LabelBubblePainter(color) },
                size = LABEL_BUBBLE_SIZE,
                stretch = LABEL_BUBBLE_STRETCH,
            )
        }
        val defaultLabelBubbleImage = image(
            value = remember { LabelBubblePainter(Color.Black) },
            size = LABEL_BUBBLE_SIZE,
            stretch = LABEL_BUBBLE_STRETCH,
        )
        SymbolLayer(
            id = MapKeys.LABEL_LAYER_ID,
            source = labelSource,
            iconImage = switch(
                input = Feature[MapKeys.COLOR_PROPERTY].cast<StringValue>(),
                cases = allDocLabelColors.map { color ->
                    case(color.toHexRgb(), labelBubbleImages.getValue(color))
                },
                fallback = defaultLabelBubbleImage,
            ),
            iconTextFit = const(IconTextFit.Both),
            iconTextFitPadding = const(DpPadding(left = 8.dp, top = 6.dp, right = 8.dp, bottom = 8.dp)),
            iconAnchor = const(SymbolAnchor.Bottom),
            iconRotationAlignment = const(IconRotationAlignment.Viewport),
            iconPitchAlignment = const(IconPitchAlignment.Viewport),
            iconAllowOverlap = const(true),
            iconIgnorePlacement = const(true),
            iconOptional = const(true),
            textField = format(span(Feature[MapKeys.TEXT_PROPERTY].cast())),
            textColor = const(Color.Black),
            textFont = const(listOf("Noto Sans Regular")),
            textSize = const(14.sp),
            textMaxWidth = const(12.em),
            textLineHeight = const(1.2.em),
            textAnchor = const(SymbolAnchor.Bottom),
            textOffset = offset(0f, -1.35f).cast(),
            textRotationAlignment = const(TextRotationAlignment.Viewport),
            textPitchAlignment = const(TextPitchAlignment.Viewport),
            textAllowOverlap = const(true),
            textIgnorePlacement = const(true),
            textOptional = const(true),
        )

        val selectedLabelSource = rememberGeoJsonSource(
            selectedObject.labelSelectionGeoJson(draggingLabel)
        )
        val labelSelectionPainter = remember(selectionFrameColor) {
            StrokeFramePainter(selectionFrameColor)
        }
        SymbolLayer(
            id = MapKeys.LABEL_SELECTION_LAYER_ID,
            source = selectedLabelSource,
            iconImage = image(
                value = labelSelectionPainter,
                size = LABEL_BUBBLE_SIZE,
                stretch = LABEL_SELECTION_STRETCH,
            ),
            iconTextFit = const(IconTextFit.Both),
            iconTextFitPadding = const(DpPadding(left = 9.dp, top = 7.dp, right = 9.dp, bottom = 11.dp)),
            iconAnchor = const(SymbolAnchor.Bottom),
            iconRotationAlignment = const(IconRotationAlignment.Viewport),
            iconPitchAlignment = const(IconPitchAlignment.Viewport),
            iconAllowOverlap = const(true),
            iconIgnorePlacement = const(true),
            iconOptional = const(true),
            textField = format(span(Feature[MapKeys.TEXT_PROPERTY].cast())),
            textOpacity = const(0f),
            textFont = const(listOf("Noto Sans Bold")),
            textSize = const(14.sp),
            textMaxWidth = const(12.em),
            textLineHeight = const(1.2.em),
            textAnchor = const(SymbolAnchor.Bottom),
            textOffset = offset(0f, -1.35f).cast(),
            textRotationAlignment = const(TextRotationAlignment.Viewport),
            textPitchAlignment = const(TextPitchAlignment.Viewport),
            textAllowOverlap = const(true),
            textIgnorePlacement = const(true),
            textOptional = const(true),
        )
    }
    LaunchedEffect(mapState, initialDocument) {
        val viewport = mapState.awaitViewport()
        initialDocument.cameraPositionFor(viewport.size)?.let(mapState::setCameraPosition)
    }

    val mapGesturesEnabled = draggingPin == null &&
            draggingLabel == null &&
            draggingTrackPoint == null &&
            draggingAreaPoint == null
    val mapInteractions = MapInteractions(
        from = if (mapGesturesEnabled) MapInteractions.Standard else MapInteractions.None,
    ) {
        callbacks {
            click {
                onEvent { event ->
                    val position = event.position ?: return@onEvent ClickResult.Pass
                    coroutineScope.launch {
                        val foregroundFeatures = mapState.queryRenderedFeatures(
                            rect = event.screenOffset.let { DpRect(it.x, it.y, it.x, it.y) },
                            layerIds = setOf(
                                MapKeys.PIN_LAYER_ID,
                                MapKeys.TRACK_POINT_LAYER_ID,
                                MapKeys.AREA_POINT_LAYER_ID,
                                MapKeys.LABEL_LAYER_ID,
                            )
                        )
                        val trackIds = mapState.queryRenderedFeatures(
                            rect = event.screenOffset.toExpandedRect(TRACK_HIT_AREA_WIDTH / 2),
                            layerIds = MapKeys.TRACK_LINE_LAYER_IDS,
                        ).findDocumentObjectIds()
                        val areaIds = mapState.queryRenderedFeatures(
                            rect = event.screenOffset.let { DpRect(it.x, it.y, it.x, it.y) },
                            layerIds = setOf(MapKeys.AREA_FILL_LAYER_ID),
                        ).findDocumentObjectIds()
                        val ids = buildList {
                            addAll(foregroundFeatures.findDocumentObjectIds())
                            addAll(trackIds)
                            addAll(areaIds)
                        }.distinct()
                        val areaBorderIds = mapState.queryRenderedFeatures(
                            rect = event.screenOffset.toExpandedRect(AREA_BORDER_HIT_WIDTH / 2),
                            layerIds = MapKeys.AREA_LINE_LAYER_IDS,
                        ).findDocumentObjectIds()
                        viewModel.clickOnMap(position, ids, areaBorderIds)
                    }
                    ClickResult.Consume
                }
            }
        }
    }
    val mapUiOptions = remember(mapGesturesEnabled) {
        if (mapGesturesEnabled) {
            MapUiOptions {
                bindings {
                    scroll {
                        mappings {
                            on(
                                modifiers = Containing(KeyModifier.Ctrl),
                                response = ScrollResponse.Zoom,
                            )
                            otherwise(ScrollResponse.Pan)
                        }
                    }
                }
            }
        } else {
            MapUiOptions.None
        }
    }

    val uiState = AppUiState(
        doc = doc,
        mode = mode,
        selectedColor = selectedColor,
        selectedLineThickness = selectedLineThickness,
        selectedLineStyle = selectedLineStyle,
        labelText = labelText,
        selectedObject = selectedObject,
        draggingPin = draggingPin,
        draggingLabel = draggingLabel,
        trackDraft = trackDraft,
        draggingTrackPoint = draggingTrackPoint,
        areaDraft = areaDraft,
        draggingAreaPoint = draggingAreaPoint,
        canUndo = canUndo,
        canRedo = canRedo,
        lockIsClosed = lock,
    )

    val callbacks = remember(viewModel) {
        AppCallbacks(
            onKeyClick = viewModel::onKeyClicked,
            selectMode = viewModel::selectMode,
            selectColor = viewModel::selectColor,
            selectLineThickness = viewModel::selectLineThickness,
            selectLineStyle = viewModel::selectLineStyle,
            setLabelText = viewModel::setLabelText,
            removeSelectedObject = viewModel::removeSelectedObject,
            undo = viewModel::undo,
            redo = viewModel::redo,
            switchLock = viewModel::switchLock,
            startDragLabel = viewModel::startDragLabel,
            updateDragLabel = viewModel::updateDragLabel,
            cancelDragLabel = viewModel::cancelDragLabel,
            finishDragLabel = viewModel::finishDragLabel,
            startDragPin = viewModel::startDragPin,
            updateDragPin = viewModel::updateDragPin,
            cancelDragPin = viewModel::cancelDragPin,
            finishDragPin = viewModel::finishDragPin,
            startDragTrackPoint = viewModel::startDragTrackPoint,
            updateDragTrackPoint = viewModel::updateDragTrackPoint,
            cancelDragTrackPoint = viewModel::cancelDragTrackPoint,
            finishDragTrackPoint = viewModel::finishDragTrackPoint,
            removeTrackPoint = viewModel::removeTrackPoint,
            startDragAreaPoint = viewModel::startDragAreaPoint,
            updateDragAreaPoint = viewModel::updateDragAreaPoint,
            cancelDragAreaPoint = viewModel::cancelDragAreaPoint,
            finishDragAreaPoint = viewModel::finishDragAreaPoint,
            removeAreaPoint = viewModel::removeAreaPoint,
        )
    }

    AppContent(
        mapState = mapState,
        uiState = uiState,
        callbacks = callbacks,
        mapInteractions = mapInteractions,
        mapUiOptions = mapUiOptions,
        onCursorPositionChange = { cursorPosition = it },
    )
}

private val InteractiveGeoJsonOptions = GeoJsonOptions(synchronousUpdate = true)
